/**
 * WalletAddressEditor — composant permettant à un membre d'enregistrer ou
 * modifier son adresse Solana ET le jeton dans lequel il veut être payé.
 *
 * 🪤 UNE seule adresse pour les deux jetons : sur Solana c'est le compte de
 * jetons associé qui diffère, pas l'adresse du portefeuille, et Cyrano le crée
 * au besoin (treasury.py::build_and_send_transfer). Ne jamais ajouter un
 * second champ d'adresse « pour l'USDT ».
 *
 * Intégré dans WalletTab.tsx (self-service, le membre édite sa propre
 * adresse) et MemberPaiementsTab.tsx (manager avec team.manage_compensation,
 * lecture/édition de l'adresse d'un autre membre).
 *
 * Feature: payroll withdraw (retrait USDC self-service).
 */
import { useEffect, useState, type ReactNode } from "react";
import { useMutation } from "convex/react";
import { CheckCircle2, Loader2 } from "@/shared/ui/icons";
import { Button, Champ, Saisie, Section } from "@/shared/ui";
import { SolanaLogo } from "@dozka/ui";
import { useTranslation } from "react-i18next";
import type { Id } from "@/../convex/_generated/dataModel";
import { api } from "@/../convex/_generated/api";
// Le validateur EST celui de Convex, pas un miroir : un miroir dérive, et
// c'est une dérive de ce genre qui a laissé passer une adresse TRON.
import {
  validateSolanaAddress,
  isTronAddress,
} from "@/../convex/_lib/solana";

/** USDT seul (Kayla, 05-10-2026 : « on retire USDC, c'est que USDT ») —
 *  miroir de `treasury.PAYOUT_TOKENS` côté Cyrano. Le choix a disparu de
 *  l'écran ; le champ Convex reste, toujours à USDT. */
const DEFAULT_PAYOUT_TOKEN = "USDT" as const;

interface WalletAddressEditorProps {
  membershipId: Id<"accountMembers">;
  currentWalletAddress: string | null;
  /** `null` pour tout membre antérieur au 19-08-2026 → USDC. */
  currentPayoutToken?: string | null;
  canEdit: boolean;
}

export function WalletAddressEditor({
  membershipId,
  currentWalletAddress,
  currentPayoutToken,
  canEdit,
}: WalletAddressEditorProps) {
  const { t } = useTranslation();
  const setWalletAddress = useMutation(api.members.setWalletAddress);

  const [draft, setDraft] = useState<string>(currentWalletAddress ?? "");
  const [tokenDraft, setTokenDraft] = useState<"USDT">(
    DEFAULT_PAYOUT_TOKEN,
  );
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<
    { kind: "ok"; message: string } | { kind: "error"; message: string } | null
  >(null);

  // Sync le draft quand on change de membre.
  useEffect(() => {
    setDraft(currentWalletAddress ?? "");
    setTokenDraft(DEFAULT_PAYOUT_TOKEN);
    setFeedback(null);
  }, [membershipId, currentWalletAddress, currentPayoutToken]);

  const trimmed = draft.trim();
  // Le bouton Sauver apparaît aussi quand SEUL le jeton change — sinon
  // basculer USDC→USDT sans toucher à l'adresse ne se sauverait jamais.
  const changed =
    trimmed !== (currentWalletAddress ?? "") ||
    tokenDraft !== (currentPayoutToken ?? DEFAULT_PAYOUT_TOKEN);
  // Validation live — MÊME fonction que celle du serveur, importée telle
  // quelle. Sert au ✓ vert « adresse Solana valide » pendant la frappe ; la
  // validation autoritaire reste server-side (setWalletAddress).
  const looksValid = validateSolanaAddress(trimmed);
  const looksTron = !looksValid && isTronAddress(trimmed);

  async function handleSave() {
    if (!canEdit || saving) return;
    setSaving(true);
    setFeedback(null);
    try {
      await setWalletAddress({
        membershipId,
        usdcWalletAddress: trimmed === "" ? undefined : trimmed,
        payoutToken: tokenDraft,
      });
      setFeedback({
        kind: "ok",
        message: t("panels.organisation.wallet.walletSaved", {
          defaultValue: "Adresse wallet sauvegardée",
        }),
      });
    } catch (err) {
      const errMessage =
        err instanceof Error ? err.message : String(err);
      // Détecte le code INVALID_WALLET_ADDRESS retourné par Convex.
      const isTronRejected = errMessage.includes("WALLET_ADDRESS_IS_TRON");
      const isInvalidAddress =
        errMessage.includes("INVALID_WALLET_ADDRESS") ||
        errMessage.toLowerCase().includes("invalid");
      setFeedback({
        kind: "error",
        message: isTronRejected
          ? t("panels.organisation.wallet.tronAddress", {
              defaultValue:
                "C'est une adresse TRON (TRC-20). Les versements passent par Solana — colle ton adresse Solana, elle reçoit l'USDT.",
            })
          : isInvalidAddress
            ? t("panels.organisation.wallet.invalidAddress", {
                defaultValue:
                  "Adresse invalide — doit être une adresse Solana base58 de 32 octets",
              })
            : errMessage,
      });
    } finally {
      setSaving(false);
    }
  }

  // Ce qui se lit sous le champ : une erreur (rouge) remplace l'aide (grise).
  const tronMsg = t("panels.organisation.wallet.tronAddress", {
    defaultValue:
      "C'est une adresse TRON (TRC-20). Les versements passent par Solana — colle ton adresse Solana, elle reçoit l'USDT.",
  });
  let erreur: string | undefined;
  let aide: ReactNode;
  if (feedback?.kind === "error") {
    erreur = feedback.message;
  } else if (feedback?.kind === "ok") {
    aide = (
      <span className="flex items-center gap-1 font-medium text-[color:var(--color-valide)]">
        <CheckCircle2 className="h-3.5 w-3.5" weight="fill" />
        {feedback.message}
      </span>
    );
  } else if (trimmed !== "" && looksValid) {
    aide = (
      <span className="flex items-center gap-1 font-medium text-[color:var(--color-valide)]">
        <CheckCircle2 className="h-3.5 w-3.5" weight="fill" />
        {t("panels.organisation.wallet.walletValid", {
          defaultValue: "Adresse Solana valide",
        })}
      </span>
    );
  } else if (trimmed !== "") {
    erreur = looksTron
      ? tronMsg
      : t("panels.organisation.wallet.walletFormatHint", {
          defaultValue: "Format d'adresse Solana attendu (base58, 32 octets)",
        });
  } else {
    aide = t("panels.organisation.wallet.walletHelp", {
      defaultValue: "Adresse Solana (base58) — elle reçoit l'USDT",
    });
  }

  const etiquetteDuChamp = t("panels.organisation.wallet.payoutTokenUsdtOnly", {
    defaultValue: "Versement en USDT (Solana)",
  });
  const placeholder = t("panels.organisation.wallet.walletPlaceholder", {
    defaultValue: "Adresse Solana base58...",
  });

  return (
    <Section
      titre={t("panels.organisation.wallet.walletLabel", {
        defaultValue: "Wallet Solana",
      })}
      icone={<SolanaLogo className="h-3.5 w-3.5" />}
      actions={
        canEdit && changed ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSave()}
            disabled={saving}
            icon={saving ? <Loader2 className="h-3 w-3 animate-spin" /> : undefined}
          >
            {t("common.save", { defaultValue: "Sauver" })}
          </Button>
        ) : undefined
      }
    >
      {canEdit ? (
        // L'adresse se TAPE : un champ. La famille Saisie n'a pas de champ d'adresse
        // modifiable (ChampCopiable est en lecture), donc `Champ`.
        <Champ
          etiquette={etiquetteDuChamp}
          taille="sm"
          className="font-mono"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setFeedback(null);
          }}
          disabled={saving}
          placeholder={placeholder}
          aria-label={t("panels.organisation.wallet.walletAriaLabel", {
            defaultValue: "Adresse Solana",
          })}
          aide={aide}
          erreur={erreur}
        />
      ) : (
        // Lecture seule (un manager qui regarde l'adresse d'un autre) : l'adresse se
        // lit EN ENTIER (elle défile, jamais coupée) et se copie d'un clic.
        <Saisie.ChampCopiable
          etiquette={etiquetteDuChamp}
          taille="sm"
          mono
          texte={t("common.copy", { defaultValue: "Copier" })}
          texteCopie={t("common.copied", { defaultValue: "Copié" })}
          valeur={draft}
          vide={placeholder}
          aide={
            erreur !== undefined ? (
              <span className="text-[color:var(--color-erreur)]">{erreur}</span>
            ) : (
              aide
            )
          }
        />
      )}
    </Section>
  );
}
